import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { useBoolean } from 'usehooks-ts';
import { Button, DialogActions, DialogContent, Stack } from '@mui/material';

import { getApiErrorMessage } from 'helper/getApiErrorMessage';
import { dialogPropsSelector } from 'store/entities/ui/slice';
import { Input } from 'components/simple';
import { Dialog } from 'components/complex';

const RENAME_FORM_FIELDS = {
  CURRENT_NAME: 'current_name',
  NEW_NAME: 'new_name',
};

const defaultValues = {
  [RENAME_FORM_FIELDS.CURRENT_NAME]: '',
  [RENAME_FORM_FIELDS.NEW_NAME]: '',
};

const styles = {
  actions: ({ breakpoints }) => ({
    flexDirection: 'column',
    justifyContent: 'flex-start',
    padding: 3,
    gap: 1,
    width: '100%',
    alignItems: 'stretch',

    [breakpoints.up('sm')]: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 3,
    },

    button: {
      margin: 'unset !important',
    },
  }),
  content: ({ breakpoints }) => ({
    paddingBottom: 0,
    [breakpoints.up('md')]: {
      paddingBottom: 4,
    },
  }),
};

export const RenameDialog = () => {
  const { dialogId, dialogTitle, currentName = '', onRename, onCloseDialog } = useSelector(dialogPropsSelector);

  const { value: isLoading, setTrue: startLoading, setFalse: stopLoading } = useBoolean();
  const [apiError, setApiError] = useState(null);

  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = useForm({
    defaultValues: {
      ...defaultValues,
      [RENAME_FORM_FIELDS.CURRENT_NAME]: currentName,
    },
    mode: 'onChange',
  });

  const clearApiErrors = () => {
    setApiError(null);
  };

  const onSubmit = async (values) => {
    try {
      startLoading();
      setApiError(null);

      const name = values[RENAME_FORM_FIELDS.NEW_NAME].trim();

      await onRename(name);

      onCloseDialog();
    } catch (error) {
      stopLoading();
      setApiError(getApiErrorMessage(error));
    }
  };

  const formattedApiError = apiError ? { message: apiError } : null;

  const formInputs = [
    {
      name: RENAME_FORM_FIELDS.CURRENT_NAME,
      label: 'Current Name',
      value: currentName,
      readOnly: true,
    },
    {
      name: RENAME_FORM_FIELDS.NEW_NAME,
      rules: {
        required: 'New Name is required',
        minLength: 2,
        validate: (value, formValues) =>
          value !== formValues[RENAME_FORM_FIELDS.CURRENT_NAME] || 'This name is already used',
      },
      label: 'New Name',
      autoFocus: true,
    },
  ];

  return (
    <Dialog
      withCustomContent
      id={`${dialogId}-dialog`}
      title={dialogTitle}
      onClose={onCloseDialog}
      isLoading={isLoading}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={styles.content}>
          <Stack spacing={4}>
            {formInputs.map(({ name, rules, ...inputProps }) => (
              <Controller
                key={name}
                name={name}
                rules={rules}
                control={control}
                render={({ field: { ref, onChange, ...field }, fieldState: { error } }) => {
                  const fieldError = error || (name === RENAME_FORM_FIELDS.NEW_NAME && formattedApiError) || null;

                  return (
                    <Input
                      {...field}
                      {...inputProps}
                      inputProps={RENAME_FORM_FIELDS.NEW_NAME ? { maxLength: 100 } : {}}
                      onChange={(value) => {
                        clearApiErrors();
                        onChange(value);
                      }}
                      id={`${dialogId}-form-${name}`}
                      innerRef={ref}
                      error={fieldError}
                    />
                  );
                }}
              />
            ))}
          </Stack>
        </DialogContent>

        <DialogActions sx={styles.actions}>
          <Button onClick={onCloseDialog} color="secondary" disabled={isLoading}>
            Back
          </Button>

          <Button type="submit" disabled={!isValid} loading={isLoading}>
            Confirm
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
